/* eslint-disable @typescript-eslint/no-explicit-any */
import { AlignCenter, AlignLeft, AlignRight, Plus, Play, X } from "lucide-react";
import type { ReactNode } from "react";

import { Button, Input, Select } from "../ds";
import { PALETTES, currentVariant, specFor, type ChartVariant } from "./catalog";
import { ChartTypeGallery } from "./ChartTypeGallery";
import { ColorRulesSection, PieLabelControls, ReferenceLinesSection, TrendLineSection } from "./ChartExtrasSections";
import { ApplyAllRow, ColorButton, ColorRow, NumberRow, Row, Section, SegRow, SliderRow, ToggleRow, getIn, setIn, type ApplyDisplayPatch } from "./FormatControls";
import { ICON_NAMES, ICON_PATHS } from "./iconSet";
import { MatrixFormatSections, TableLayoutSection } from "./MatrixFormatSections";
import { isBarWidget, type SWidget } from "./model";
import { NumberFormatSection, UnitsOverride } from "./NumberFormatSection";
import { fromLegacyFormat, takesNumberFormat, type NumberFormat } from "./numberFormat";

/* Format pane (contract v2.1 section 2). Every control writes exactly the
 * contract key noted beside it; nothing is written until the user touches a
 * control (unset = the Look's default, shown greyed as "auto"). */

const AXIS = new Set(["bar_chart", "line_chart", "area_chart", "combo_chart", "waterfall_chart"]);
const LINES = new Set(["line_chart", "area_chart", "combo_chart"]);
const PIES = new Set(["donut_chart", "pie_chart"]);
const CARTESIAN = new Set(["bar_chart", "line_chart", "area_chart", "combo_chart"]);
const LEGEND = new Set(["bar_chart", "line_chart", "area_chart", "combo_chart", "donut_chart", "pie_chart"]);
const LABELS = new Set([...AXIS, "treemap_chart", "funnel_chart"]);
const BAR_LABEL_POS = new Set(["bar_chart", "combo_chart", "waterfall_chart"]);
const ANIMATED_EXTRA = new Set(["kpi", "gauge_chart", "table", "matrix", "grouped_table", "status_list"]);

/** What "Automatic" animation means for this type (contract: auto rules). */
function autoAnimation(type: string): string {
  if (type === "bar_chart" || type === "combo_chart" || type === "waterfall_chart" || type === "funnel_chart") return "bars grow up";
  if (type === "line_chart" || type === "area_chart") return "lines draw in";
  if (type === "donut_chart" || type === "pie_chart" || type === "gauge_chart") return "sweeps round";
  return "fades in";
}

const FONT_DEFAULT = { title: 13, axis: 12, labels: 12 };

function colName(m: any): string {
  return typeof m === "string" ? m : m?.name ?? m?.key ?? String(m);
}

const HEADER_STYLE_KEYS = ["background", "color", "fontSize", "fontWeight", "fontFamily", "uppercase", "align"];

/** Icon grid for display.titleStyle.icon (same inline SVGs the renderer draws). */
function IconPicker({ value, onChange }: { value: string | undefined; onChange: (v: string | undefined) => void }) {
  return (
    <div role="listbox" aria-label="Title icon" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(30px, 1fr))", gap: 4 }}>
      <button type="button" role="option" aria-selected={!value} title="No icon" className={`st-arr__seg${!value ? " is-on" : ""}`} style={{ height: 30, padding: 0, outline: !value ? "2px solid var(--ds-accent, #2563eb)" : undefined }} onClick={() => onChange(undefined)}>
        <X size={14} />
      </button>
      {ICON_NAMES.map((name) => (
        <button key={name} type="button" role="option" aria-selected={value === name} title={name.replace(/-/g, " ")} className={`st-arr__seg${value === name ? " is-on" : ""}`} style={{ height: 30, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", outline: value === name ? "2px solid var(--ds-accent, #2563eb)" : undefined }} onClick={() => onChange(name)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{ __html: ICON_PATHS[name] }} />
        </button>
      ))}
    </div>
  );
}

/** display.tablePresentation.headerStyle / bodyStyle (table; header also grouped_table + matrix). */
function TableStyleSections({ t, set, d, onApplyDisplayPatch }: { t: string; set: (path: string[], value: any, live?: boolean) => void; d: any; onApplyDisplayPatch?: ApplyDisplayPatch }) {
  const hs = (k: string) => getIn(d, ["tablePresentation", "headerStyle", k]);
  const bs = (k: string) => getIn(d, ["tablePresentation", "bodyStyle", k]);
  const hsSet = (k: string, v: any, live = false) => set(["tablePresentation", "headerStyle", k], v, live);
  const bsSet = (k: string, v: any, live = false) => set(["tablePresentation", "bodyStyle", k], v, live);
  const hasHeader = getIn(d, ["tablePresentation", "headerStyle"]) !== undefined;
  const hasBody = getIn(d, ["tablePresentation", "bodyStyle"]) !== undefined;
  const headerValue = () => {
    const out: Record<string, unknown> = {};
    for (const k of HEADER_STYLE_KEYS) out[k] = hs(k); // undefined = clears that key on the target
    return out;
  };
  return (
    <>
      <Section id="tblheader" title="Header" summary={hasHeader ? "Custom" : "From the Look"} canReset={hasHeader} onReset={() => set(["tablePresentation", "headerStyle"], undefined)}>
        <Row label="Font">
          <Select inputSize="sm" value={(hs("fontFamily") as string) ?? "default"} onChange={(e) => hsSet("fontFamily", e.target.value === "default" ? undefined : e.target.value)}>
            <option value="default">Report font (default)</option>
            <option value="condensed">Condensed</option>
            <option value="serif">Serif</option>
            <option value="mono">Monospace</option>
          </Select>
        </Row>
        <SliderRow label="Text size" value={hs("fontSize")} defaultValue={13} min={10} max={40} unit="px" onChange={(v) => hsSet("fontSize", v, true)} />
        <SegRow label="Weight" value={(hs("fontWeight") ?? "bold") as string} onChange={(v) => hsSet("fontWeight", v)} options={[{ value: "normal", label: "Regular" }, { value: "semibold", label: "Semi-bold" }, { value: "bold", label: "Bold" }]} />
        <ColorRow label="Text colour" value={hs("color")} onChange={(v) => hsSet("color", v, true)} />
        <ColorRow label="Background" value={hs("background")} allowTransparent onChange={(v) => hsSet("background", v, true)} />
        <SegRow label="Alignment" hint="Per-column alignment (Columns) still wins." value={(hs("align") ?? "left") as string} onChange={(v) => hsSet("align", v === "left" ? undefined : v)} options={[{ value: "left", label: <AlignLeft size={14} />, title: "Left" }, { value: "center", label: <AlignCenter size={14} />, title: "Centre" }, { value: "right", label: <AlignRight size={14} />, title: "Right" }]} />
        <ToggleRow label="CAPITAL LETTERS" checked={!!hs("uppercase")} onChange={(v) => hsSet("uppercase", v || undefined)} />
        {onApplyDisplayPatch && hasHeader ? <ApplyAllRow what="table" onApply={(scope) => onApplyDisplayPatch(["tablePresentation", "headerStyle"], headerValue(), scope, ["table", "grouped_table", "matrix"])} /> : null}
      </Section>
      {t === "table" || t === "grouped_table" ? (
        <Section id="tblbody" title="Body" summary={hasBody ? "Custom" : "From the Look"} canReset={hasBody} onReset={() => set(["tablePresentation", "bodyStyle"], undefined)}>
          <SliderRow label="Text size" value={bs("fontSize")} defaultValue={13} min={10} max={40} unit="px" onChange={(v) => bsSet("fontSize", v, true)} />
          <ColorRow label="Text colour" value={bs("color")} onChange={(v) => bsSet("color", v, true)} />
          <ToggleRow label="Zebra stripes" hint="Shade every other row." checked={!!bs("zebra")} onChange={(v) => bsSet("zebra", v || undefined)} />
          {bs("zebra") ? <ColorRow label="Stripe colour" value={bs("zebraColor")} onChange={(v) => bsSet("zebraColor", v, true)} /> : null}
          <SegRow label="Row spacing" value={(bs("rowPadding") ?? "normal") as string} onChange={(v) => bsSet("rowPadding", v === "normal" ? undefined : v)} options={[{ value: "compact", label: "Compact" }, { value: "normal", label: "Normal" }, { value: "roomy", label: "Roomy" }]} />
        </Section>
      ) : null}
    </>
  );
}

/** Per-column width (% of the table), header label and alignment -- written to
 * display.tablePresentation.{columnWidths,columnLabels,columnAlign}, which the
 * TV/preview renderer already honours. */
function TableColumnsSection({ widget, set, setMany, d }: { widget: SWidget; set: (path: string[], value: any, live?: boolean) => void; setMany: (pairs: Array<[string[], any]>, live?: boolean) => void; d: any }) {
  const cols: string[] = [...(widget.query?.dimensions ?? []), ...(widget.query?.measures ?? [])].map(colName);
  if (cols.length === 0) return null;
  const tp = d.tablePresentation ?? {};
  const widths: Record<string, string> = tp.columnWidths ?? {};
  const labels: Record<string, string> = tp.columnLabels ?? {};
  const aligns: Record<string, string> = tp.columnAlign ?? {};
  const customised = Object.keys(widths).length + Object.keys(labels).length + Object.keys(aligns).length;
  const pct = (c: string): number | null => {
    const v = widths[c];
    return v ? parseFloat(v) : null;
  };
  const total = cols.reduce((s, c) => s + (pct(c) ?? 0), 0);
  const equal = () => {
    const each = Math.floor((100 / cols.length) * 10) / 10;
    const next: Record<string, string> = {};
    cols.forEach((c) => (next[c] = `${each}%`));
    set(["tablePresentation", "columnWidths"], next);
  };
  return (
    <Section
      id="columns"
      title="Columns"
      summary={customised ? `${customised} custom` : "Auto"}
      canReset={customised > 0}
      onReset={() =>
        setMany([
          [["tablePresentation", "columnWidths"], undefined],
          [["tablePresentation", "columnLabels"], undefined],
          [["tablePresentation", "columnAlign"], undefined],
        ])
      }
    >
      <div className="st-frow-pair" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <button type="button" className="st-arr__seg" onClick={equal}>Equal widths</button>
        <button type="button" className="st-arr__seg" onClick={() => set(["tablePresentation", "columnWidths"], undefined)}>Auto widths</button>
        {total > 0 ? <span className="st-frow__hint" style={{ marginLeft: "auto" }}>{Math.round(total)}% set</span> : null}
      </div>
      {cols.map((c) => (
        <div key={c} className="st-frow-pair" style={{ borderTop: "1px solid var(--ds-border, #e2e8f0)", paddingTop: 6, marginTop: 6 }}>
          <Row label={<strong style={{ fontWeight: 600 }}>{c}</strong>}>
            <input
              className="ds-input ds-input--sm"
              placeholder="Header text"
              defaultValue={labels[c] ?? ""}
              key={`${c}-${labels[c] ?? ""}`}
              onBlur={(e) => set(["tablePresentation", "columnLabels", c], e.target.value.trim() || undefined)}
            />
          </Row>
          <NumberRow
            label="Width (%)"
            value={pct(c)}
            placeholder="Auto"
            onChange={(n) => set(["tablePresentation", "columnWidths", c], n == null || n <= 0 ? undefined : `${Math.min(100, n)}%`)}
          />
          <SegRow
            label="Align"
            value={(aligns[c] as "auto" | "left" | "center" | "right") ?? "auto"}
            options={[
              { value: "auto", label: "Auto" },
              { value: "left", label: "Left" },
              { value: "center", label: "Center" },
              { value: "right", label: "Right" },
            ]}
            onChange={(v) => set(["tablePresentation", "columnAlign", c], v === "auto" ? undefined : v)}
          />
        </div>
      ))}
    </Section>
  );
}

export function FormatPane({ widget, seriesNames, onChange, onSwitchVariant, onReplay, onApplyNumberFormatToPage, onApplyDisplayPatch, multi }: { widget: SWidget; /** Several visuals selected: "same" hides the visual-type gallery, "mixed" shows only the sections every kind shares (title, card). */ multi?: "same" | "mixed"; seriesNames: string[]; onChange: (patch: Partial<SWidget>, key?: string) => void; onSwitchVariant: (v: ChartVariant) => void; onReplay: () => void; onApplyNumberFormatToPage?: (nf: NumberFormat) => void; onApplyDisplayPatch?: ApplyDisplayPatch }) {
  const spec = multi === "mixed" ? undefined : specFor(widget.type);
  const t = multi === "mixed" ? "common" : widget.type;
  const d = widget.display ?? {};
  const c = d.chart ?? {};
  const isChart = spec?.category === "Charts";
  const inBar = isBarWidget(widget);
  const isFilter = spec?.group === "filter";

  /** Write display.<path>. Continuous controls (sliders, colours, text) pass
   * `live` so a drag becomes ONE undo step (history coalesces by key). */
  const set = (path: string[], value: any, live = false) => onChange({ display: setIn(d, path, value) }, live ? `fmt-${widget.id}-${path.join(".")}` : undefined);
  const setMany = (pairs: Array<[string[], any]>, live = false) => {
    let next = d;
    for (const [p, v] of pairs) next = setIn(next, p, v);
    onChange({ display: next }, live ? `fmt-${widget.id}-${pairs.map((p) => p[0].join(".")).join("+")}` : undefined);
  };
  const has = (path: string[]) => getIn(d, path) !== undefined;
  const ts = (k: string) => getIn(d, ["titleStyle", k]);
  const cs = (k: string) => getIn(d, ["cardStyle", k]);
  const ch = (...p: string[]) => getIn(c, p);

  const labelsOn = ch("dataLabels", "show") ?? !!c.showValueLabels;
  const yTitle = ch("yAxis", "title") ?? c.primaryAxis?.title ?? "";
  const palette = (c.seriesColors as string[] | undefined) ?? PALETTES[0].colors;
  // Known series (one per measure) -> one swatch each; legend splits / slices -> first 6.
  const slots = seriesNames.length ? Math.min(8, seriesNames.length) : 6;
  const anim = ch("animation", "type") ?? "auto";
  const duration = ch("animation", "durationMs") as number | undefined;
  const bands = (ch("gauge", "bands") as Array<{ to: number; color: string }> | undefined) ?? [];

  const replaySoon = () => window.setTimeout(onReplay, 260);

  return (
    <div className="st-format">
      {/* ---------- Visual type ---------- */}
      {spec && !multi && !inBar && (isChart || t === "kpi" || t === "gauge_chart" || t === "kpi_trend") ? (
        <Section id="type" title="Visual type" defaultOpen summary={currentVariant(t, c)?.label ?? spec.label}>
          <ChartTypeGallery widget={widget} onSwitch={onSwitchVariant} />
        </Section>
      ) : null}

      {/* ---------- Title ---------- */}
      {!inBar ? (
        <Section id="title" title="Title" defaultOpen summary={d.showTitle === false ? "Hidden" : ts("fontSize") ? `${ts("fontSize")} px${ts("color") ? " · colour" : ""}` : has(["titleStyle"]) ? "Custom" : "Default"} canReset={has(["titleStyle"]) || has(["titleTemplate"])} onReset={() => setMany([[["titleStyle"], undefined], [["titleTemplate"], undefined]])}>
          <ToggleRow label="Show the title" checked={d.showTitle !== false} onChange={(v) => set(["showTitle"], v)} />
          {d.showTitle !== false ? (
            <>
              <Row label="Dynamic title" hint="Optional. {monthShort} and {year} follow the Month/Year filter." stacked>
                <Input inputSize="sm" value={(d.titleTemplate as string) ?? ""} placeholder="e.g. Cost — {monthShort} {year}" onChange={(e) => set(["titleTemplate"], e.target.value || undefined, true)} />
              </Row>
              <SliderRow label="Text size" value={ts("fontSize")} defaultValue={FONT_DEFAULT.title} min={10} max={48} unit="px" onChange={(v) => set(["titleStyle", "fontSize"], v, true)} />
              <SegRow label="Weight" value={(ts("fontWeight") ?? "semibold") as string} onChange={(v) => set(["titleStyle", "fontWeight"], v)} options={[{ value: "normal", label: "Regular" }, { value: "semibold", label: "Semi-bold" }, { value: "bold", label: "Bold" }]} />
              <Row label="Font">
                <Select inputSize="sm" value={(ts("fontFamily") as string) ?? "default"} onChange={(e) => set(["titleStyle", "fontFamily"], e.target.value === "default" ? undefined : e.target.value)}>
                  <option value="default">Report font (default)</option>
                  <option value="condensed">Condensed — fits long titles</option>
                  <option value="serif">Serif — classic, formal</option>
                  <option value="mono">Monospace — codes, IDs</option>
                </Select>
              </Row>
              <ColorRow label="Text colour" value={ts("color")} onChange={(v) => set(["titleStyle", "color"], v, true)} />
              <ColorRow label="Title background" hint="A band behind the title." value={ts("background")} allowTransparent onChange={(v) => set(["titleStyle", "background"], v, true)} autoLabel="None" />
              {onApplyDisplayPatch && ts("background") ? <ApplyAllRow what="card title" onApply={(scope) => onApplyDisplayPatch(["titleStyle", "background"], ts("background"), scope)} /> : null}
              <Row label="Icon" hint="Shown before the title." stacked>
                <IconPicker value={ts("icon")} onChange={(v) => set(["titleStyle", "icon"], v)} />
              </Row>
              {ts("icon") ? (
                <>
                  <ColorRow label="Icon colour" value={ts("iconColor")} onChange={(v) => set(["titleStyle", "iconColor"], v, true)} autoLabel="Title colour" />
                  <SliderRow label="Icon size" value={ts("iconSize")} defaultValue={16} min={10} max={48} unit="px" onChange={(v) => set(["titleStyle", "iconSize"], v, true)} />
                  {onApplyDisplayPatch ? <ApplyAllRow what="card title icon" onApply={(scope) => onApplyDisplayPatch(["titleStyle"], { icon: ts("icon"), iconColor: ts("iconColor"), iconSize: ts("iconSize") }, scope)} /> : null}
                </>
              ) : null}
              <SegRow label="Alignment" value={(ts("align") ?? "left") as string} onChange={(v) => set(["titleStyle", "align"], v === "left" ? undefined : v)} options={[{ value: "left", label: <AlignLeft size={14} />, title: "Left" }, { value: "center", label: <AlignCenter size={14} />, title: "Centre" }, { value: "right", label: <AlignRight size={14} />, title: "Right" }]} />
              <ToggleRow label="CAPITAL LETTERS" checked={!!ts("uppercase")} onChange={(v) => set(["titleStyle", "uppercase"], v || undefined)} />
              <ToggleRow label={<em>Italic</em>} checked={!!ts("italic")} onChange={(v) => set(["titleStyle", "italic"], v || undefined)} />
            </>
          ) : null}
        </Section>
      ) : null}

      {/* ---------- Card ---------- */}
      {!inBar ? (
        <Section id="card" title="Card (background & border)" summary={cs("background") ? "Custom" : "From the Look"} canReset={has(["cardStyle"]) || d.showBorder === false} onReset={() => setMany([[["cardStyle"], undefined], [["showBorder"], undefined]])}>
          <ColorRow label="Background" value={cs("background")} allowTransparent onChange={(v) => set(["cardStyle", "background"], v, true)} />
          <ToggleRow label="Show a border" checked={d.showBorder !== false} onChange={(v) => set(["showBorder"], v)} />
          {d.showBorder !== false ? (
            <>
              <ColorRow label="Border colour" value={cs("borderColor")} onChange={(v) => set(["cardStyle", "borderColor"], v, true)} />
              <SliderRow label="Border thickness" value={cs("borderWidth")} defaultValue={1} min={0} max={4} unit="px" onChange={(v) => set(["cardStyle", "borderWidth"], v, true)} />
            </>
          ) : null}
          <SliderRow label="Rounded corners" value={cs("radius")} defaultValue={8} min={0} max={24} unit="px" onChange={(v) => set(["cardStyle", "radius"], v, true)} />
          <SegRow label="Shadow" value={(cs("shadow") ?? "none") as string} onChange={(v) => set(["cardStyle", "shadow"], v === "none" ? undefined : v)} options={[{ value: "none", label: "None" }, { value: "soft", label: "Soft" }, { value: "strong", label: "Strong" }]} />
          <SegRow label="Space inside" value={(cs("padding") ?? "normal") as string} onChange={(v) => set(["cardStyle", "padding"], v === "normal" ? undefined : v)} options={[{ value: "compact", label: "Compact" }, { value: "normal", label: "Normal" }, { value: "roomy", label: "Roomy" }]} />
          <ToggleRow label="Show “last refreshed” time" checked={d.showLastRefresh !== false} onChange={(v) => set(["showLastRefresh"], v)} />
        </Section>
      ) : null}

      {/* ---------- X axis (category axis) ---------- */}
      {AXIS.has(t) ? (
        <Section id="xaxis" title={c.orientation === "horizontal" ? "Category axis (down the side)" : "X axis (categories)"} summary={ch("xAxis", "show") === false ? "Hidden" : "Shown"} canReset={!!c.xAxis} onReset={() => set(["chart", "xAxis"], undefined)}>
          <ToggleRow label="Show category labels" checked={ch("xAxis", "show") !== false} onChange={(v) => set(["chart", "xAxis", "show"], v ? undefined : false)} />
          {ch("xAxis", "show") !== false ? (
            <>
              <SliderRow label="Label size" value={ch("xAxis", "labelFontSize")} defaultValue={FONT_DEFAULT.axis} min={8} max={28} unit="px" onChange={(v) => set(["chart", "xAxis", "labelFontSize"], v, true)} />
              <ColorRow label="Label colour" value={ch("xAxis", "labelColor")} onChange={(v) => set(["chart", "xAxis", "labelColor"], v, true)} />
              {c.orientation !== "horizontal" ? (
                <SegRow label="Tilt labels" hint="Tilt when long names overlap." value={String(ch("xAxis", "labelRotation") ?? 0)} onChange={(v) => set(["chart", "xAxis", "labelRotation"], v === "0" ? undefined : Number(v))} options={[{ value: "0", label: "Flat" }, { value: "-45", label: "45°" }, { value: "-90", label: "Upright" }]} />
              ) : null}
            </>
          ) : null}
          <Row label="Axis title" hint="Leave empty for none." stacked>
            <Input inputSize="sm" value={(ch("xAxis", "title") as string) ?? ""} placeholder="e.g. Month" onChange={(e) => set(["chart", "xAxis", "title"], e.target.value || undefined, true)} />
          </Row>
        </Section>
      ) : null}

      {/* ---------- Y axis (value axis) ---------- */}
      {AXIS.has(t) ? (
        <Section id="yaxis" title={c.orientation === "horizontal" ? "Value axis (along the bottom)" : "Y axis (values)"} summary={ch("yAxis", "gridlines") === false ? "No gridlines" : "Gridlines on"} canReset={!!c.yAxis || !!c.primaryAxis?.title} onReset={() => setMany([[["chart", "yAxis"], undefined], [["chart", "primaryAxis", "title"], undefined]])}>
          <ToggleRow label="Show value labels" checked={ch("yAxis", "show") !== false} onChange={(v) => set(["chart", "yAxis", "show"], v ? undefined : false)} />
          {ch("yAxis", "show") !== false ? (
            <>
              <SliderRow label="Label size" value={ch("yAxis", "labelFontSize")} defaultValue={FONT_DEFAULT.axis} min={8} max={28} unit="px" onChange={(v) => set(["chart", "yAxis", "labelFontSize"], v, true)} />
              <ColorRow label="Label colour" value={ch("yAxis", "labelColor")} onChange={(v) => set(["chart", "yAxis", "labelColor"], v, true)} />
            </>
          ) : null}
          <Row label="Axis title" hint="Leave empty for none." stacked>
            {/* primaryAxis.title mirrored so TVs on the old renderer keep showing it */}
            <Input inputSize="sm" value={yTitle} placeholder="e.g. Cost (USD)" onChange={(e) => setMany([[["chart", "yAxis", "title"], e.target.value || undefined], [["chart", "primaryAxis", "title"], e.target.value || undefined]], true)} />
          </Row>
          {ch("yAxis", "show") !== false ? (
            <>
              <div className="st-fsub">Numbers on the axis</div>
              <UnitsOverride widget={widget} path={["chart", "yAxis"]} onChange={onChange} />
            </>
          ) : null}
          <div className="st-fsub">Range</div>
          <div className="st-frow-pair">
            <NumberRow label="Start at" value={ch("yAxis", "min")} onChange={(v) => set(["chart", "yAxis", "min"], v ?? undefined)} />
            <NumberRow label="End at" value={ch("yAxis", "max")} onChange={(v) => set(["chart", "yAxis", "max"], v ?? undefined)} />
          </div>
          <div className="st-fsub">Gridlines</div>
          <ToggleRow label="Show gridlines" checked={ch("yAxis", "gridlines") !== false} onChange={(v) => set(["chart", "yAxis", "gridlines"], v ? undefined : false)} />
          {ch("yAxis", "gridlines") !== false ? (
            <>
              <ColorRow label="Gridline colour" value={ch("yAxis", "gridColor")} onChange={(v) => set(["chart", "yAxis", "gridColor"], v, true)} />
              <SegRow label="Gridline style" value={(ch("yAxis", "gridStyle") ?? "solid") as string} onChange={(v) => set(["chart", "yAxis", "gridStyle"], v === "solid" ? undefined : v)} options={[{ value: "solid", label: "Solid" }, { value: "dashed", label: "Dashed" }, { value: "dotted", label: "Dotted" }]} />
            </>
          ) : null}
        </Section>
      ) : null}

      {/* ---------- Reference lines & trend line (v2.8) ---------- */}
      {CARTESIAN.has(t) ? <ReferenceLinesSection c={c} set={set} seriesNames={seriesNames} dual={t === "combo_chart"} /> : null}
      {CARTESIAN.has(t) && c.orientation !== "horizontal" ? <TrendLineSection c={c} set={set} seriesNames={seriesNames} /> : null}

      {t === "bar_chart" && seriesNames.length <= 1 ? <ColorRulesSection c={c} set={set} /> : null}

      {/* ---------- Data labels ---------- */}
      {LABELS.has(t) ? (
        <Section id="labels" title="Data labels" summary={labelsOn ? "On" : "Off"} canReset={!!c.dataLabels || !!c.showValueLabels} onReset={() => setMany([[["chart", "dataLabels"], undefined], [["chart", "showValueLabels"], undefined]])}>
          {/* legacy showValueLabels mirrored for TVs on the old renderer */}
          <ToggleRow label="Show values on the chart" checked={labelsOn} onChange={(v) => setMany([[["chart", "dataLabels", "show"], v], [["chart", "showValueLabels"], v || undefined]])} />
          {labelsOn ? (
            <>
              {BAR_LABEL_POS.has(t) ? (
                <Row label="Position" stacked>
                  <div className="st-pos">
                    {([
                      ["outsideEnd", "Above the bar", "Outside end"],
                      ["insideEnd", "Inside, at the top", "Inside end"],
                      ["center", "In the middle", "Centre"],
                      ["insideBase", "Inside, at the bottom", "Inside base"],
                    ] as const).map(([v, title, label]) => {
                      const on = (ch("dataLabels", "position") ?? "outsideEnd") === v;
                      return (
                        <button key={v} type="button" className={`st-pos__btn${on ? " is-on" : ""}`} aria-pressed={on} title={title} onClick={() => set(["chart", "dataLabels", "position"], v)}>
                          <PosGlyph pos={v} />
                          <span>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </Row>
              ) : null}
              <SliderRow label="Text size" value={ch("dataLabels", "fontSize")} defaultValue={FONT_DEFAULT.labels} min={8} max={28} unit="px" onChange={(v) => set(["chart", "dataLabels", "fontSize"], v, true)} />
              <ColorRow label="Text colour" value={ch("dataLabels", "color")} onChange={(v) => set(["chart", "dataLabels", "color"], v, true)} />
              <UnitsOverride widget={widget} path={["chart", "dataLabels"]} onChange={onChange} legacyFull />
            </>
          ) : null}
        </Section>
      ) : null}

      {/* ---------- Slice labels (donut / pie) ---------- */}
      {PIES.has(t) ? (
        <Section id="slices" title="Slice labels" summary={c.labelMode === "external" ? "On the slices" : c.labelMode === "none" ? "Off" : "Legend"} canReset={!!c.labelMode || !!c.dataLabels || !!c.pieLabels} onReset={() => setMany([[["chart", "labelMode"], undefined], [["chart", "dataLabels"], undefined], [["chart", "pieLabels"], undefined]])}>
          <SegRow label="Name each slice" value={(c.labelMode ?? "legend") as string} onChange={(v) => set(["chart", "labelMode"], v)} options={[{ value: "legend", label: "In a legend" }, { value: "external", label: "On the slices" }, { value: "none", label: "Don't" }]} />
          {c.labelMode === "external" ? (
            <>
              <SliderRow label="Text size" value={ch("dataLabels", "fontSize")} defaultValue={FONT_DEFAULT.labels} min={8} max={28} unit="px" onChange={(v) => set(["chart", "dataLabels", "fontSize"], v, true)} />
              <ColorRow label="Text colour" value={ch("dataLabels", "color")} onChange={(v) => set(["chart", "dataLabels", "color"], v, true)} />
            </>
          ) : null}
          <Button size="sm" variant="subtle" onClick={() => setMany([[["chart", "dataLabels", "units"], "none"], [["numberFormat"], { ...(d.numberFormat ?? fromLegacyFormat(d.format)), units: "none" }], [["chart", "labelMode"], "legend"], [["chart", "legendPosition"], "top"], [["chart", "legendColumns"], 1], [["chart", "legendLabelMaxChars"], undefined], [["chart", "legendShowValues"], false], [["chart", "legendShowPercent"], false], [["chart", "showLegend"], true], [["chart", "pieLabels"], { show: true, content: "both", position: "outside", fontSize: 7, percentDecimals: 2 }]])}>Top legend + outside values</Button>
          <PieLabelControls c={c} set={set} />
        </Section>
      ) : null}

      {/* ---------- Legend ---------- */}
      {LEGEND.has(t) && !(PIES.has(t) && c.labelMode && c.labelMode !== "legend") ? (
        <Section id="legend" title="Legend" summary={c.legendPosition === "bottom" ? "Bottom" : c.legendPosition === "beside" ? "Beside" : "Top"} canReset={!!c.legendPosition || c.showLegend === false || c.legendFontSize !== undefined} onReset={() => setMany([[["chart", "legendPosition"], undefined], [["chart", "showLegend"], undefined], [["chart", "legendFontSize"], undefined]])}>
          <ToggleRow label="Show the legend" checked={c.showLegend !== false} onChange={(v) => set(["chart", "showLegend"], v ? undefined : false)} />
          {PIES.has(t) ? <><ToggleRow label="Percentages in legend" checked={c.legendShowPercent !== false} onChange={(v) => set(["chart", "legendShowPercent"], v)} /><ToggleRow label="Values in legend" checked={!!c.legendShowValues} onChange={(v) => set(["chart", "legendShowValues"], v)} /></> : null}
          <SegRow label="Where" hint={PIES.has(t) ? "Category names; slice values can appear outside separately." : "Shown when there is more than one series."} value={(c.legendPosition ?? "top") as string} onChange={(v) => set(["chart", "legendPosition"], v)} options={[{ value: "top", label: "Top" }, { value: "bottom", label: "Bottom" }, { value: "beside", label: "Beside" }]} />
          <SliderRow label="Text size" value={c.legendFontSize} defaultValue={12} min={8} max={24} unit="px" onChange={(v) => set(["chart", "legendFontSize"], v, true)} />
        </Section>
      ) : null}

      {/* ---------- Colours ---------- */}
      {isChart ? (
        <Section id="colours" title="Colours" summary={<span className="st-sw-mini">{palette.slice(0, 5).map((x, i) => <i key={i} style={{ background: x }} />)}</span>} canReset={!!c.seriesColors} onReset={() => set(["chart", "seriesColors"], undefined)}>
          <div className="st-fsub" style={{ marginTop: 0 }}>Palette</div>
          <div className="ds-stack" style={{ gap: 5 }}>
            {PALETTES.map((p) => {
              const active = p.id === "maridive" ? !c.seriesColors : JSON.stringify(c.seriesColors ?? []) === JSON.stringify(p.colors);
              return (
                <button key={p.id} type="button" className={`st-palette-row${active ? " is-active" : ""}`} onClick={() => set(["chart", "seriesColors"], p.id === "maridive" ? undefined : p.colors)}>
                  <span className="ds-row" style={{ gap: 3 }}>{p.colors.slice(0, 7).map((x) => <span key={x} style={{ width: 14, height: 14, borderRadius: 3, background: x }} />)}</span>
                  <span className="ds-small">{p.label}</span>
                </button>
              );
            })}
          </div>
          <div className="st-fsub">{PIES.has(t) || t === "treemap_chart" || t === "funnel_chart" ? "Colour of each slice, in order" : "Colour of each series"}</div>
          <div className="st-series">
            {Array.from({ length: slots }).map((_, i) => (
              <div key={i} className="st-series__row">
                <ColorButton compact value={(c.seriesColors as string[] | undefined)?.[i] ?? palette[i % palette.length]} onChange={(v) => {
                  const base = [...((c.seriesColors as string[] | undefined) ?? PALETTES[0].colors)];
                  while (base.length <= i) base.push(PALETTES[0].colors[base.length % PALETTES[0].colors.length]);
                  base[i] = v ?? PALETTES[0].colors[i % PALETTES[0].colors.length];
                  set(["chart", "seriesColors"], base, true);
                }} />
                <span className="ds-small ds-truncate">{seriesNames[i] ?? `${ordinal(i + 1)} ${PIES.has(t) ? "slice" : "series"}`}</span>
              </div>
            ))}
          </div>
          {!seriesNames.length ? <div className="ds-small ds-faint">Further {PIES.has(t) || t === "treemap_chart" || t === "funnel_chart" ? "slices" : "series"} repeat the palette in order.</div> : null}
        </Section>
      ) : null}

      {/* ---------- Lines & shape ---------- */}
      {LINES.has(t) || t === "donut_chart" || t === "gauge_chart" ? (
        <Section id="shape" title={t === "gauge_chart" ? "Gauge range & target" : t === "donut_chart" ? "Donut shape" : "Lines & shape"} canReset={["lineStyle", "showMarkers", "lineWidth", "fillOpacity", "donutThickness", "gauge"].some((k) => c[k] !== undefined)} onReset={() => setMany(["lineStyle", "showMarkers", "lineWidth", "fillOpacity", "donutThickness", "gauge"].map((k) => [["chart", k], undefined] as [string[], any]))}>
          {LINES.has(t) ? (
            <>
              <Row label="Line shape" stacked>
                <div className="st-pos st-pos--3">
                  {(["straight", "smooth", "step"] as const).map((v) => {
                    const on = (c.lineStyle ?? "straight") === v;
                    return (
                      <button key={v} type="button" className={`st-pos__btn${on ? " is-on" : ""}`} aria-pressed={on} onClick={() => set(["chart", "lineStyle"], v === "straight" ? undefined : v)}>
                        <LineGlyph kind={v} />
                        <span>{v === "straight" ? "Straight" : v === "smooth" ? "Smooth" : "Steps"}</span>
                      </button>
                    );
                  })}
                </div>
              </Row>
              <SliderRow label="Line thickness" value={c.lineWidth} defaultValue={2} min={1} max={6} unit="px" onChange={(v) => set(["chart", "lineWidth"], v, true)} />
              <ToggleRow label="Dots on each point" checked={!!c.showMarkers} onChange={(v) => set(["chart", "showMarkers"], v || undefined)} />
            </>
          ) : null}
          {t === "area_chart" ? (
            <SliderRow label="Fill strength" hint="How solid the area under the line is." value={c.fillOpacity == null ? undefined : Math.round(c.fillOpacity * 100)} defaultValue={30} min={5} max={90} unit="%" onChange={(v) => set(["chart", "fillOpacity"], v === undefined ? undefined : Math.round(v) / 100, true)} />
          ) : null}
          {t === "donut_chart" ? (
            <SliderRow label="Ring thickness" hint="Thin ring ↔ almost a pie." value={c.donutThickness == null ? undefined : Math.round(c.donutThickness * 100)} defaultValue={45} min={15} max={80} unit="%" onChange={(v) => set(["chart", "donutThickness"], v === undefined ? undefined : Math.round(v) / 100, true)} />
          ) : null}
          {t === "gauge_chart" ? (
            <>
              <div className="st-frow-pair">
                <NumberRow label="Starts at" value={ch("gauge", "min")} placeholder="0" onChange={(v) => set(["chart", "gauge", "min"], v ?? undefined)} />
                <NumberRow label="Ends at" value={ch("gauge", "max")} placeholder="Auto" onChange={(v) => set(["chart", "gauge", "max"], v ?? undefined)} />
              </div>
              <NumberRow label="Target" hint="Draws a marker on the dial." value={ch("gauge", "target")} placeholder="None" onChange={(v) => set(["chart", "gauge", "target"], v ?? undefined)} />
              <div className="st-fsub">Colour bands</div>
              <div className="ds-small ds-faint" style={{ marginBottom: 6 }}>E.g. red up to 50, amber up to 80, green to the end.</div>
              {bands.map((b, i) => (
                <div key={`${i}-${b.to}`} className="st-band">
                  <ColorButton compact value={b.color} onChange={(v) => set(["chart", "gauge", "bands"], bands.map((x, j) => (j === i ? { ...x, color: v ?? "#94a3b8" } : x)), true)} />
                  <span className="ds-small ds-muted">up to</span>
                  <input className="ds-input ds-input--sm st-numbox" defaultValue={b.to} inputMode="decimal" onBlur={(e) => { const n = parseFloat(e.target.value); if (Number.isFinite(n)) set(["chart", "gauge", "bands"], bands.map((x, j) => (j === i ? { ...x, to: n } : x)).sort((a, z) => a.to - z.to)); }} aria-label="Band ends at" />
                  <button type="button" className="st-mini" aria-label="Remove band" onClick={() => set(["chart", "gauge", "bands"], bands.filter((_, j) => j !== i).length ? bands.filter((_, j) => j !== i) : undefined)}><X size={12} /></button>
                </div>
              ))}
              <Button size="sm" variant="ghost" icon={Plus} onClick={() => {
                const colors = ["#ef4444", "#eab308", "#22c55e"];
                const last = bands[bands.length - 1]?.to ?? 0;
                set(["chart", "gauge", "bands"], [...bands, { to: last + (bands.length ? 25 : 50), color: colors[Math.min(bands.length, 2)] }]);
              }}>Add a band</Button>
            </>
          ) : null}
        </Section>
      ) : null}

      {/* ---------- v2.3 visuals ---------- */}
      {t === "scatter_chart" || t === "bullet_chart" || t === "kpi_trend" || t === "heatmap_chart" ? (
        <Section
          id="v23"
          title={t === "scatter_chart" ? "Dots & trend" : t === "bullet_chart" ? "Target" : t === "kpi_trend" ? "Number & trend" : "Colours & values"}
          canReset={["trendLine", "bullet", "kpiTrend", "heatmap"].some((k) => c[k] !== undefined)}
          onReset={() => setMany(["trendLine", "bullet", "kpiTrend", "heatmap"].map((k) => [["chart", k], undefined] as [string[], any]))}
        >
          {t === "scatter_chart" ? (
            <ToggleRow label="Trend line" hint="Straight best-fit line through the dots." checked={!!c.trendLine} onChange={(v) => set(["chart", "trendLine"], v || undefined)} />
          ) : null}
          {t === "bullet_chart" ? (
            <>
              <ToggleRow label="Show % of target" checked={ch("bullet", "showPercent") !== false} onChange={(v) => set(["chart", "bullet", "showPercent"], v ? undefined : false)} />
              <div className="st-frow-pair">
                <NumberRow label="Green from" hint="% of target" value={ch("bullet", "goodAt") == null ? null : Math.round(ch("bullet", "goodAt") * 100)} placeholder="100" onChange={(v) => set(["chart", "bullet", "goodAt"], v == null ? undefined : v / 100)} />
                <NumberRow label="Amber from" hint="% of target" value={ch("bullet", "warnAt") == null ? null : Math.round(ch("bullet", "warnAt") * 100)} placeholder="80" onChange={(v) => set(["chart", "bullet", "warnAt"], v == null ? undefined : v / 100)} />
              </div>
            </>
          ) : null}
          {t === "kpi_trend" ? (
            <>
              <SegRow label="Big number" value={(ch("kpiTrend", "value") ?? "total") as "total" | "last" | "average"} options={[{ value: "total", label: "Total", title: "Sum of every period (flows: revenue, cost)" }, { value: "last", label: "Latest", title: "Last period only (balances: headcount, cash)" }, { value: "average", label: "Average" }]} onChange={(v) => set(["chart", "kpiTrend", "value"], v === "total" ? undefined : v)} />
              <ToggleRow label="Up is good" hint="Off for costs: a rise shows red." checked={ch("kpiTrend", "upIsGood") !== false} onChange={(v) => set(["chart", "kpiTrend", "upIsGood"], v ? undefined : false)} />
              <ToggleRow label="Sparkline" checked={ch("kpiTrend", "showSparkline") !== false} onChange={(v) => set(["chart", "kpiTrend", "showSparkline"], v ? undefined : false)} />
            </>
          ) : null}
          {t === "heatmap_chart" ? (
            <>
              <ToggleRow label="Numbers in the cells" checked={ch("heatmap", "showValues") !== false} onChange={(v) => set(["chart", "heatmap", "showValues"], v ? undefined : false)} />
              <SegRow label="Colour scale" value={(ch("heatmap", "scale") ?? "auto") as "auto" | "sequential" | "diverging"} options={[{ value: "auto", label: "Auto" }, { value: "sequential", label: "Low → high" }, { value: "diverging", label: "− / +", title: "Negatives red, positives in the Look colour" }]} onChange={(v) => set(["chart", "heatmap", "scale"], v === "auto" ? undefined : v)} />
            </>
          ) : null}
        </Section>
      ) : null}

      {/* ---------- Animation ---------- */}
      {isChart || ANIMATED_EXTRA.has(t) ? (
        <Section id="anim" title="Animation" summary={anim === "auto" ? "Automatic" : anim === "none" ? "Off" : String(anim).charAt(0).toUpperCase() + String(anim).slice(1)} canReset={!!c.animation} onReset={() => set(["chart", "animation"], undefined)}>
          <Row label="Effect" hint={anim === "auto" ? `Automatic: this visual ${autoAnimation(t)}.` : "Plays once when the page opens — never on data refresh."} stacked>
            <Select inputSize="sm" value={anim} onChange={(e) => { set(["chart", "animation", "type"], e.target.value === "auto" ? undefined : e.target.value); if (e.target.value !== "none") replaySoon(); }}>
              <option value="auto">Automatic (recommended)</option>
              <option value="none">No animation</option>
              <option value="grow">Grow — bars rise from the axis</option>
              <option value="fade">Fade in</option>
              <option value="sweep">Sweep — rings and dials fill round</option>
              <option value="draw">Draw — lines trace from left to right</option>
              <option value="pop">Pop — items bounce in</option>
            </Select>
          </Row>
          {anim !== "none" ? (
            <>
              <SegRow label="Speed" value={duration === undefined ? "normal" : duration <= 500 ? "fast" : duration >= 1300 ? "slow" : "normal"} onChange={(v) => { set(["chart", "animation", "durationMs"], v === "fast" ? 400 : v === "slow" ? 1600 : undefined); replaySoon(); }} options={[{ value: "fast", label: "Quick" }, { value: "normal", label: "Normal" }, { value: "slow", label: "Slow" }]} />
              <SliderRow label="Exact length" value={duration} defaultValue={700} min={200} max={3000} step={100} unit="ms" onChange={(v) => set(["chart", "animation", "durationMs"], v, true)} />
              <ToggleRow label="One after another" hint="Bars / slices start in turn instead of all at once." checked={ch("animation", "stagger") !== false} onChange={(v) => { set(["chart", "animation", "stagger"], v ? undefined : false); replaySoon(); }} />
              <Button size="sm" icon={Play} onClick={onReplay} style={{ alignSelf: "flex-start" }}>Replay animation</Button>
            </>
          ) : null}
        </Section>
      ) : null}

      {/* ---------- Number format (display.numberFormat) ---------- */}
      {takesNumberFormat(t) ? <NumberFormatSection widget={widget} onChange={onChange} onApplyToPage={onApplyNumberFormatToPage} /> : null}

      {/* ---------- Columns: width / label / alignment (tables) ---------- */}
      {t === "table" ? <TableColumnsSection widget={widget} set={set} setMany={setMany} d={d} /> : null}

      {/* ---------- Matrix text / fit / look / totals; table layout ---------- */}
      {t === "matrix" ? <MatrixFormatSections widget={widget} set={set} setMany={setMany} d={d} /> : null}
      {t === "table" || t === "grouped_table" ? <TableLayoutSection widget={widget} set={set} setMany={setMany} d={d} /> : null}

      {/* ---------- Header & body style (tables) ---------- */}
      {t === "table" || t === "grouped_table" || t === "matrix" ? <TableStyleSections t={t} set={set} d={d} onApplyDisplayPatch={onApplyDisplayPatch} /> : null}

      {/* ---------- Data bars (tables, v2.5) ---------- */}
      {t === "table" && (widget.query?.measures?.length ?? 0) > 0 ? (
        <Section id="databars" title="Data bars" summary={Object.keys(getIn(d, ["tablePresentation", "dataBars"]) ?? {}).length ? "On" : "Off"} canReset={!!getIn(d, ["tablePresentation", "dataBars"])} onReset={() => set(["tablePresentation", "dataBars"], undefined)}>
          {(widget.query?.measures ?? []).map((m: any) => {
            const col = typeof m === "string" ? m : m?.name ?? m?.key ?? String(m);
            const cfg = getIn(d, ["tablePresentation", "dataBars", col]);
            return (
              <div key={col} className="st-frow-pair">
                <ToggleRow label={`Bars in “${col}”`} checked={!!cfg} onChange={(v) => set(["tablePresentation", "dataBars", col], v ? { color: "#22c55e" } : undefined)} />
                {cfg ? <ColorRow label="Bar colour" value={cfg.color} onChange={(v) => set(["tablePresentation", "dataBars", col, "color"], v ?? "#22c55e", true)} /> : null}
              </div>
            );
          })}
        </Section>
      ) : null}

      {/* ---------- Filter placement ---------- */}
      {isFilter ? (
        <Section id="filterpos" title="Placement" defaultOpen>
          <ToggleRow label="Keep inside the page grid" hint="Off = shown in the filter bar at the top of the screen." checked={!!d.keepInCanvas} onChange={(v) => set(["keepInCanvas"], v || undefined)} />
          {t === "filter" ? (
            <SegRow label="Style" hint="Buttons = one tile per value, like a Power BI slicer. Best inside the page grid." value={(d.slicerStyle as string) ?? "dropdown"} onChange={(v) => set(["slicerStyle"], v === "dropdown" ? undefined : v)} options={[{ value: "dropdown", label: "Dropdown" }, { value: "buttons", label: "Buttons" }]} />
          ) : null}
        </Section>
      ) : null}
    </div>
  );
}

function ordinal(n: number): string {
  return n === 1 ? "1st" : n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`;
}

function PosGlyph({ pos }: { pos: string }): ReactNode {
  const y = pos === "outsideEnd" ? 4 : pos === "insideEnd" ? 12 : pos === "center" ? 19 : 27;
  return (
    <svg width="30" height="32" viewBox="0 0 30 32" aria-hidden="true">
      <rect x="8" y="9" width="14" height="22" rx="1.5" fill="currentColor" opacity="0.28" />
      <rect x="10" y={y - 2.5} width="10" height="5" rx="1" fill="currentColor" />
    </svg>
  );
}

function LineGlyph({ kind }: { kind: "straight" | "smooth" | "step" }): ReactNode {
  const d = kind === "straight" ? "M3 22 L12 10 L20 17 L29 6" : kind === "smooth" ? "M3 22 C8 22 8 10 12 10 S17 17 20 17 S25 6 29 6" : "M3 22 H9 V10 H17 V17 H24 V6 H29";
  return (
    <svg width="32" height="26" viewBox="0 0 32 26" aria-hidden="true">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
